import { useEffect, useRef } from "react";
import i18next from "i18next";

import { usePatchUserPreferencesMutation } from "@shared/api/server/gs3Server";
import type { User } from "@shared/api/server/gs3Server";
import { setThemePreference } from "@shared/hooks";
import { clearPendingLocale, getPendingLocale } from "@shared/utils";

import {
  normalizeSupportedLanguageCode,
  resolveSupportedLanguageCode,
} from "../utils/resolveSupportedLanguageCode";
import { shouldReloadPageOnGoogleMapsRoute } from "../utils/shouldReloadPageOnGoogleMapsRoute";
import { useLocalization } from "./useLocalization";

const currentLanguage = () => resolveSupportedLanguageCode(i18next);

const onMapsRoute = () =>
  typeof window !== "undefined" &&
  shouldReloadPageOnGoogleMapsRoute(window.location.pathname);

export const useHydrateAccountPreferences = (user: User | null | undefined) => {
  const { changeLanguage } = useLocalization();
  const { mutateAsync } = usePatchUserPreferencesMutation();
  const hydratedUserId = useRef<string | null>(null);
  const themedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!user || hydratedUserId.current === user.id) {
      return;
    }

    hydratedUserId.current = user.id;
    const pending = getPendingLocale();
    const storedLanguage = user.userPreferences?.language;
    const storedTheme = user.userPreferences?.theme;

    if (themedUserId.current !== user.id) {
      themedUserId.current = user.id;
      if (storedTheme === "light" || storedTheme === "dark") {
        setThemePreference(storedTheme);
      } else {
        setThemePreference(null);
      }
    }

    const languageBeforeHydrate = currentLanguage();

    void (async () => {
      try {
        if (pending) {
          if (currentLanguage() !== pending) {
            await changeLanguage(pending, { reload: false });
          }
          if (currentLanguage() !== pending) {
            clearPendingLocale();
            return;
          }

          await mutateAsync({ language: pending });

          const chosenWhileSaving = currentLanguage();
          if (chosenWhileSaving !== pending) {
            await mutateAsync({ language: chosenWhileSaving });
          }

          clearPendingLocale();
          if (onMapsRoute() && currentLanguage() === pending) {
            window.location.reload();
          }
          return;
        }

        if (currentLanguage() !== languageBeforeHydrate) {
          return;
        }

        const nextLanguage =
          typeof storedLanguage === "string"
            ? (normalizeSupportedLanguageCode(storedLanguage) ?? "pt-BR")
            : "pt-BR";
        await changeLanguage(nextLanguage);
      } catch {
        if (!pending) {
          return;
        }
        if (currentLanguage() === pending) {
          await changeLanguage(languageBeforeHydrate, { reload: false });
          return;
        }
        clearPendingLocale();
      }
    })();
  }, [user, changeLanguage, mutateAsync]);
};
